import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { dbFiles } from '../../App';
import { useLoader } from '../../universal/context/LoaderContext';
import { useNotification } from '../../universal/context/NotificationContext';
import { UserContext } from '../../universal/context/UserContext';

// Prompt interface
interface Prompt {
  id: string;
  name: string;
  text: string;
  category?: string;
}

const PromptsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [showNewPromptPopup, setShowNewPromptPopup] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptText, setNewPromptText] = useState('');

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState('');
  const [editingPromptName, setEditingPromptName] = useState('');
  const [editingPromptText, setEditingPromptText] = useState('');

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletePromptId, setDeletePromptId] = useState('');
  const [deletePromptName, setDeletePromptName] = useState('');

  // State for categories
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [showNewCategoryPopup, setShowNewCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(''); // For new/edit prompt
  const [editingPromptCategory, setEditingPromptCategory] = useState(''); // For edit prompt

  // State for filtering and search
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);

  useEffect(() => {
    fetchPrompts();
    fetchCategories(); // Fetch categories on component mount
  }, []);

  // Apply filters when prompts, searchQuery, or categoryFilter changes
  useEffect(() => {
    applyFilters(searchQuery, categoryFilter);
  }, [prompts, searchQuery, categoryFilter]);

  const fetchPrompts = async () => {
    showLoader();
    try {
      const colRef = collection(dbFiles, 'prompts');
      const snapshot = await getDocs(colRef);
      const data = snapshot.docs.map(d => ({
        id: d.id,
        ...(d.data() as Omit<Prompt, 'id'>),
        category: d.data().category || 'Uncategorized' // Assign default category
      }));
      setPrompts(data);
    } catch (error) {
      console.error(error);
      showNotification('Failed to load prompts!', 'error');
    } finally {
      hideLoader();
    }
  };

  const fetchCategories = async () => {
    showLoader();
    const categoriesCollectionRef = collection(dbFiles, 'promptCategories'); // New collection
    try {
      const querySnapshot = await getDocs(categoriesCollectionRef);
      const fetchedCategories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as { name: string })
      }));
      setCategories(fetchedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showNotification('Failed to load categories!', 'error');
    } finally {
      hideLoader();
    }
  };

  const openNewPrompt = () => {
    setNewPromptName('');
    setNewPromptText('');
    setSelectedCategory(''); // Reset selected category
    setShowNewPromptPopup(true);
  };

  const saveNewPrompt = async () => {
    if (!newPromptName || !newPromptText) {
      showNotification('Please fill out both fields!', 'error');
      return;
    }
    showLoader();
    try {
      const colRef = collection(dbFiles, 'prompts');
      await addDoc(colRef, {
        name: newPromptName,
        text: newPromptText,
        category: selectedCategory || 'Uncategorized' // Save with category
      });
      showNotification('Prompt created!', 'success');
      setShowNewPromptPopup(false);
      fetchPrompts();
    } catch (error) {
      console.error(error);
      showNotification('Failed to create prompt!', 'error');
    } finally {
      hideLoader();
    }
  };

  const openEditPrompt = (prompt: Prompt) => {
    setEditingPromptId(prompt.id);
    setEditingPromptName(prompt.name);
    setEditingPromptText(prompt.text);
    setEditingPromptCategory(prompt.category || 'Uncategorized'); // Set current category for editing
    setShowEditPopup(true);
  };

  const saveEditPrompt = async () => {
    if (!editingPromptName || !editingPromptText) {
      showNotification('Please fill out both fields!', 'error');
      return;
    }
    showLoader();
    try {
      const promptRef = doc(dbFiles, 'prompts', editingPromptId);
      await updateDoc(promptRef, {
        name: editingPromptName,
        text: editingPromptText,
        category: editingPromptCategory || 'Uncategorized' // Update with new category
      });
      showNotification('Prompt updated!', 'success');
      setShowEditPopup(false);
      fetchPrompts();
    } catch (error) {
      console.error(error);
      showNotification('Failed to update prompt!', 'error');
    } finally {
      hideLoader();
    }
  };

  const openDeleteConfirmation = (prompt: Prompt) => {
    setDeletePromptId(prompt.id);
    setDeletePromptName(prompt.name);
    setShowDeleteConfirmation(true);
  };

  const confirmDeletePrompt = async () => {
    showLoader();
    try {
      const promptRef = doc(dbFiles, 'prompts', deletePromptId);
      await deleteDoc(promptRef);
      showNotification('Prompt deleted!', 'success');
      setShowDeleteConfirmation(false);
      fetchPrompts();
    } catch (error) {
      console.error(error);
      showNotification('Failed to delete prompt!', 'error');
    } finally {
      hideLoader();
    }
  };

  // Functions for managing categories
  const openNewCategoryPopup = () => {
    setNewCategoryName('');
    setShowNewCategoryPopup(true);
  };

  const handleHideNewCategoryPopup = () => {
    setShowNewCategoryPopup(false);
    setNewCategoryName('');
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showNotification('Category name cannot be empty!', 'error');
      return;
    }
    showLoader();
    const newCategoryRef = doc(dbFiles, 'promptCategories', newCategoryName.trim());
    try {
      const docSnapshot = await getDoc(newCategoryRef);
      if (docSnapshot.exists()) {
        setNewCategoryName('');
        showNotification('Category ' + newCategoryName.trim() + ' already exists!', 'error');
      } else {
        await setDoc(newCategoryRef, { name: newCategoryName.trim() });
        setCategories([...categories, { id: newCategoryName.trim(), name: newCategoryName.trim() }]);
        setNewCategoryName('');
        setShowNewCategoryPopup(false);
        showNotification('Category ' + newCategoryName.trim() + ' added successfully!', 'success');
        // If adding category from a prompt popup, set it as selected
        if (showNewPromptPopup) setSelectedCategory(newCategoryName.trim());
        if (showEditPopup) setEditingPromptCategory(newCategoryName.trim());
      }
    } catch (error) {
      showNotification('Failed to add category!', 'error');
    } finally {
      hideLoader();
    }
  };

  const applyFilters = (query: string, category: string) => {
    let currentPrompts = [...prompts]; // Start with all prompts

    if (query.trim() !== '') {
      currentPrompts = currentPrompts.filter(
        (prompt) =>
          prompt.name.toLowerCase().includes(query.toLowerCase()) ||
          prompt.id.toLowerCase().includes(query.toLowerCase()) // Optional: search by ID too
          // prompt.text.toLowerCase().includes(query.toLowerCase()) // Optional: search by text
      );
    }

    if (category) {
      if (category === 'Uncategorized') {
        currentPrompts = currentPrompts.filter((prompt) => !prompt.category || prompt.category === 'Uncategorized');
      } else {
        currentPrompts = currentPrompts.filter((prompt) => prompt.category === category);
      }
    }
    setFilteredPrompts(currentPrompts);
  };

  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head">
          <button className="termsbuttonstyleadd" style={{backgroundColor: 'var(--primary-error-color)', color: '#fff'}} onClick={() => navigate('/console/automations')}>
            Back
          </button>
          <button className="termsbuttonstyleadd" style={{ marginLeft: '10px' }} onClick={openNewPrompt}>
            Create New Prompt
          </button>

          {/* New Prompt Popup */}
          {showNewPromptPopup && (
            <div className="edit-overlay z-ind-25">
              <div className="delete-confirmation-popup">
                <div className="add-client-form edit-hreflang">
                  <h3>Create New Prompt</h3>
                  <label>Name</label>
                  <input
                    type="text"
                    value={newPromptName}
                    onChange={e => setNewPromptName(e.target.value)}
                    placeholder="Prompt Name"
                  />
                  <label>Category</label>
                  <div className='flatcat' style={{ marginBottom: '15px' }}>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="">Select a category (optional)</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <div className='plus' onClick={openNewCategoryPopup} style={{ marginLeft: '8px', height: '36px', lineHeight: '36px' }}>+</div>
                  </div>
                  <label>Text</label>
                  <textarea
                    value={newPromptText}
                    onChange={e => setNewPromptText(e.target.value)}
                    placeholder="Prompt Text"
                    rows={6}
                  />
                  <div className="buttonsetcontainer hrefpopup">
                    <button className="sitemanagerbutton addhref chunkpadded" onClick={saveNewPrompt}>
                      Save
                    </button>
                    <button className="sitemanagerbutton deletehref chunkpadded" onClick={() => setShowNewPromptPopup(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Prompt Popup */}
          {showEditPopup && (
            <div className="edit-overlay z-ind-25">
              <div className="delete-confirmation-popup">
                <div className="add-client-form edit-hreflang">
                  <h3>Edit Prompt</h3>
                  <label>Name</label>
                  <input type="text" value={editingPromptName} onChange={e => setEditingPromptName(e.target.value)} />
                  <label>Category</label>
                  <div className='flatcat' style={{ marginBottom: '15px' }}>
                    <select
                      value={editingPromptCategory}
                      onChange={(e) => setEditingPromptCategory(e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <div className='plus' onClick={openNewCategoryPopup} style={{ marginLeft: '8px', height: '36px', lineHeight: '36px' }}>+</div>
                  </div>
                  <label>Text</label>
                  <textarea value={editingPromptText} onChange={e => setEditingPromptText(e.target.value)} rows={6} />
                  <div className="buttonsetcontainer hrefpopup">
                    <button className="sitemanagerbutton addhref chunkpadded" onClick={saveEditPrompt}>
                      Save
                    </button>
                    <button className="sitemanagerbutton deletehref chunkpadded" onClick={() => setShowEditPopup(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Popup */}
          {showDeleteConfirmation && (
            <div className="edit-overlay z-ind-25">
              <div className="delete-confirmation-popup">
                <p>Are you sure you want to delete "{deletePromptName}"?</p>
                <div className="buttonsetcontainer hrefpopup">
                  <button className="sitemanagerbutton deletehref chunkpadded" onClick={confirmDeletePrompt}>
                    Delete
                  </button>
                  <button className="sitemanagerbutton addhref chunkpadded" onClick={() => setShowDeleteConfirmation(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Category Popup */}
          {showNewCategoryPopup && (
            <div className="edit-overlay z-ind-25">
              <div className="delete-confirmation-popup">
                <div className="add-client-form edit-hreflang">
                  <h3>Add New Category</h3>
                  <label>Category Name</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="New Category Name"
                  />
                  <div className='buttonsetcontainer hrefpopup'>
                    <button style={{width:150}} className='sitemanagerbutton addhref chunkpadded' onClick={handleAddCategory}>Add Category</button>
                    <button style={{width:150}} className='sitemanagerbutton deletehref chunkpadded' onClick={handleHideNewCategoryPopup}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <h1>Prompts</h1>

          {/* Filter and Search Section */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', width: '100%', maxWidth: '600px' }}>
            <input 
              type="text" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              placeholder="Search Prompts by Name"
              style={{ flexGrow: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginTop: '0' }}
            />
            <div className='flatcat' style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px' }}
              >
                <option value="">All Categories</option>
                <option value="Uncategorized">Uncategorized</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <div className='plus' onClick={openNewCategoryPopup} style={{ marginLeft: '8px', height: '36px', lineHeight: '36px', cursor: 'pointer', marginBottom: '0px' }}>+</div>
            </div>
          </div>

          <div className="catblockcontainer">
            {filteredPrompts.length === 0 && !searchQuery && !categoryFilter ? (
                prompts.length === 0 ? (
                    <p>No prompts created yet. Click "Create New Prompt" to get started!</p>
                ) : (
                    <p>Loading prompts or an issue occurred. If prompts exist, try adjusting filters.</p> // Should ideally be handled by a loader state from fetchPrompts
                )
            ) : filteredPrompts.length === 0 ? (
                <p>No prompts match your current filter criteria.</p>
            ) : (
              Object.entries(
                filteredPrompts.reduce((groups: { [key: string]: Prompt[] }, prompt) => {
                  const category = prompt.category || 'Uncategorized';
                  if (!groups[category]) {
                    groups[category] = [];
                  }
                  groups[category].push(prompt);
                  return groups;
                }, {})
              ).map(([category, categoryPrompts]: [string, Prompt[]]) => (
                <div className='catblock' key={category}>
                  <h2 className='categorytitle' style={{ textTransform: 'capitalize' }}>{category.replace(/-/g, ' ')}</h2>
                  <div className='bubble-container'>
                    {categoryPrompts.map(prompt => {
                      const maxLength = 100; // Define max characters for preview
                      let previewText = prompt.text;
                      if (prompt.text.length > maxLength) {
                        previewText = prompt.text.substring(0, maxLength) + '...';
                      }
                      return (
                        <div key={prompt.id} className="bubble hrefsets">
                          <h2>{prompt.name}</h2>
                          <p style={{ marginTop: 3, whiteSpace: 'pre-line' }}>{previewText}</p>
                          <div className="buttonsetcontainer">
                            <button className="sitemanagerbutton selectedhref" onClick={() => openEditPrompt(prompt)}>
                              Edit
                            </button>
                            <button className="sitemanagerbutton deletehref" onClick={() => openDeleteConfirmation(prompt)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptsPage; 